import React, { useEffect, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';

import Body2 from '@joinforma/ui/components/Body2';
import Divider from '@joinforma/ui/components/Divider';
import Headline2 from '@joinforma/ui/components/Headline2';
import Headline3 from '@joinforma/ui/components/Headline3';
import Headline4 from '@joinforma/ui/components/Headline4';
import Title1 from '@joinforma/ui/components/Title1';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import Index from 'components/common/ProductCard';
import ProductListLoading from 'components/common/ProductListLoading';
import { EVENTS, MarketplaceSearchType } from 'constants/events';
import { logEvent } from 'utils/amplitude';
import { logRudderstackEvent } from 'utils/rudderstack';

import type { Product } from './types';

interface Company {
  features: {
    stipend_config: {
      display_as_amount: boolean;
    };
  };
}

const NarrowContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 10px auto;
  padding: 0px 20px;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    max-width: 1160px;
  }

  @media (min-width: ${ScreenWidth.LAPTOP}) {
    padding: 0;
  }
`;

const WelcomeHeader = styled.div`
  margin-bottom: 23px;
`;

const WelcomeText = styled(Headline2)``;

const RelatedResultsHeader = styled.div`
  margin-bottom: 16px;
`;

const RelatedResultsText = styled(Headline3)``;

const DividerContainer = styled.div`
  margin-bottom: 32px;
`;

const ResultsGridContainer = styled.div`
  margin-top: 16px;
  margin-bottom: 68px;
`;

const ResultsContainer = styled.div``;

const ResultsHeader = styled(Title1)`
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px 12px;
  margin: 1px 0;
  justify-content: center;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    justify-content: initial;
  }
`;

const ResultsEmptyContainer = styled.div`
  margin-bottom: 32px;
`;

const ResultsEmptyHeader = styled(Headline4)`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 5px;
`;

const ResultsEmptyMessage = styled(Body2)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ResultsEmptyLink = styled.a`
  display: inline;
  color: ${({ theme }) => theme.colors.text.link};
`;

const ProductsContainer = styled.div``;

const onClickProductCard = (
  event: string,
  product: Product,
  index: number,
  additionalProperties: Record<
    string,
    string | number | Array<string> | boolean
  > = {}
) => {
  const attributes = {
    card_position: index,
    product_id: product.product_id,
    vendor_id: product.vendor_id,
  };
  logEvent(event, attributes);
  logRudderstackEvent(event, attributes);
};

const RelatedProducts = ({
  company,
  currency,
  employee_country_exchange_rate,
  related_products,
}: {
  company: Company;
  currency: string;
  employee_country_exchange_rate: number;
  related_products: Product[];
}) => {
  useEffect(() => {
    const products_returned = related_products.map(({ id }) => id);
    logEvent(EVENTS.RelatedSearchResultsViewed, {
      related_products_returned: products_returned,
    });
    logRudderstackEvent(EVENTS.RelatedSearchResultsViewed, {
      products_returned,
    });
  }, [related_products]);

  return (
    <>
      <RelatedResultsHeader>
        <RelatedResultsText>You Might Also Like...</RelatedResultsText>
      </RelatedResultsHeader>
      <DividerContainer>
        <Divider />
      </DividerContainer>
      <ResultsGridContainer>
        <CardsContainer>
          {related_products!.map((algolia_product, index) => (
            <Index
              key={algolia_product.id}
              algoliaProduct={algolia_product}
              company={company}
              currency={currency}
              exchangeRate={employee_country_exchange_rate}
              onClick={() =>
                onClickProductCard(
                  EVENTS.RelatedSearchResultsClicked,
                  algolia_product,
                  index
                )
              }
              origin="search"
            />
          ))}
        </CardsContainer>
      </ResultsGridContainer>
    </>
  );
};

const Products = ({
  products,
  totalProducts,
  company,
  currency,
  employeeCountryExchangeRate,
  hasNextPage,
  fetchNextPage,
  relatedProducts,
  searchQuery,
  searchType,
  isEmptySearchAlternative,
}: {
  products: Product[];
  totalProducts: number;
  company: Company;
  currency: string;
  employeeCountryExchangeRate: number;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  relatedProducts?: Product[];
  searchQuery: string;
  isEmptySearchAlternative: boolean;
  searchType: MarketplaceSearchType;
}) => {
  const eventProperties = useMemo(
    () => ({
      total_results_returned: totalProducts,
      search_query: searchQuery,
      search_type: searchType,
      is_empty_search_alternative: isEmptySearchAlternative,
      products_returned: products.map(({ id }) => id),
    }),
    [totalProducts, searchQuery, searchType, isEmptySearchAlternative, products]
  );

  useEffect(() => {
    logEvent(EVENTS.SearchResultsViewed, eventProperties);
    logRudderstackEvent(EVENTS.SearchResultsViewed, eventProperties);
  }, [eventProperties]);

  const productCardOrigin =
    isEmptySearchAlternative &&
    searchType === MarketplaceSearchType.MisoSemanticSearch
      ? 'Miso Empty Search Result'
      : 'search';

  return (
    <ResultsContainer>
      {/*
        we can't use the `total_products` count here since it has dupe results
        more details here: https://linear.app/forma/issue/MKT-499
      */}
      <ResultsHeader>{`Showing ${products.length} results`}</ResultsHeader>
      <ResultsGridContainer>
        <InfiniteScroll
          loader={null}
          dataLength={products.length}
          next={() => fetchNextPage()}
          hasMore={hasNextPage}
        >
          <CardsContainer>
            {products!.map((algolia_product, index) => (
              <Index
                key={algolia_product.id}
                algoliaProduct={algolia_product}
                company={company}
                currency={currency}
                exchangeRate={employeeCountryExchangeRate}
                onClick={() =>
                  onClickProductCard(
                    EVENTS.SearchResultsClicked,
                    algolia_product,
                    index,
                    eventProperties
                  )
                }
                origin={productCardOrigin}
              />
            ))}
          </CardsContainer>
        </InfiniteScroll>
      </ResultsGridContainer>
      {relatedProducts && relatedProducts.length > 0 && (
        <RelatedProducts
          company={company}
          currency={currency}
          employee_country_exchange_rate={employeeCountryExchangeRate}
          related_products={relatedProducts}
        />
      )}
    </ResultsContainer>
  );
};

const DiscoverProducts = ({
  company,
  currency,
  suggested_products,
  search_query,
}: {
  company: Company;
  currency: string;
  suggested_products: Product[];
  search_query: string;
}) => {
  useEffect(() => {
    const products_returned = suggested_products.map(({ id }) => id);
    const total_products = products_returned.length;
    logEvent(EVENTS.DiscoverSearchResultsViewed, {
      unpopular_products_returned: products_returned,
      search_query,
      total_results_returned: total_products,
    });
    logRudderstackEvent(EVENTS.DiscoverSearchResultsViewed, {
      products_returned,
      search_query,
      total_results_returned: total_products,
    });
  }, [suggested_products, search_query]);

  return (
    <>
      <RelatedResultsHeader>
        <RelatedResultsText>Discover More of Forma</RelatedResultsText>
      </RelatedResultsHeader>
      <DividerContainer>
        <Divider />
      </DividerContainer>
      <ResultsGridContainer>
        <CardsContainer>
          {suggested_products!.map((algolia_product, index) => (
            <Index
              key={algolia_product.id}
              algoliaProduct={algolia_product}
              company={company}
              currency={currency}
              onClick={() =>
                onClickProductCard(
                  EVENTS.DiscoverSearchResultsClicked,
                  algolia_product,
                  index
                )
              }
              origin="search"
            />
          ))}
        </CardsContainer>
      </ResultsGridContainer>
    </>
  );
};

const NoProducts = ({
  company,
  currency,
  suggested_products,
  search_query,
}: {
  company: Company;
  currency: string;
  suggested_products?: Product[];
  search_query: string;
}) => (
  <ResultsContainer>
    <ResultsEmptyContainer>
      <ResultsEmptyHeader>No results found.</ResultsEmptyHeader>
      <ResultsEmptyMessage>
        Try another term or&nbsp;
        <ResultsEmptyLink
          target="_blank"
          href="https://twic.typeform.com/to/QjUpdaVG"
        >
          submit a suggestion
        </ResultsEmptyLink>
        &nbsp;to the Forma team and tell us what you'd love to see in the store.
      </ResultsEmptyMessage>
    </ResultsEmptyContainer>
    {!!suggested_products && (
      <DiscoverProducts
        search_query={search_query}
        company={company}
        currency={currency}
        suggested_products={suggested_products}
      />
    )}
  </ResultsContainer>
);

const ProductSearchResults = ({
  company,
  currency,
  products,
  employeeCountryExchangeRate,
  totalProducts,
  query,
  hasNextPage,
  fetchNextPage,
  relatedProducts,
  suggestedProducts,
  isVendorsLoading,
  isEmptySearchAlternative,
  searchType,
}: {
  company: Company;
  currency: string;
  products?: Product[];
  employeeCountryExchangeRate: number;
  totalProducts: number;
  query: string;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  relatedProducts?: Product[];
  suggestedProducts?: Product[];
  isVendorsLoading: boolean;
  isEmptySearchAlternative: boolean;
  searchType: MarketplaceSearchType;
}) => (
  <NarrowContainer>
    <WelcomeHeader>
      <WelcomeText>
        {query ? `Search for "${query}"` : 'Showing all results'}
      </WelcomeText>
    </WelcomeHeader>
    <DividerContainer>
      <Divider />
    </DividerContainer>
    {isVendorsLoading ? (
      <ProductListLoading />
    ) : (
      <ProductsContainer>
        {!totalProducts ? (
          <NoProducts
            company={company}
            currency={currency}
            suggested_products={suggestedProducts}
            search_query={query}
          />
        ) : (
          <Products
            searchQuery={query}
            products={products!}
            totalProducts={totalProducts}
            company={company}
            currency={currency}
            employeeCountryExchangeRate={employeeCountryExchangeRate}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            relatedProducts={relatedProducts}
            isEmptySearchAlternative={isEmptySearchAlternative}
            searchType={searchType}
          />
        )}
      </ProductsContainer>
    )}
  </NarrowContainer>
);
export default ProductSearchResults;
